import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { getAtlasFile, forkAtlasFile } from '../api/atlas';
import PageContainer from '../components/layout/PageContainer';
import ScrollReveal from '../components/ui/ScrollReveal';
import Button from '../components/ui/Button';
import { ArrowLeft, Calendar, MapPin, User, Edit, GitFork, Share2, Link2, MessageCircle, Download, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getSourceConfig } from '../constants/sourceTypeConfig';
import SendToConcierge from '../components/concierge/SendToConcierge';

function AtlasFileViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [atlasFile, setAtlasFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images: [], index: 0 }
  const coverRef = useRef(null);
  const contentRef = useRef(null);
  const shareRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: coverRef,
    offset: ['start start', 'end start'],
  });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const coverOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);

  useEffect(() => {
    fetchAtlasFile();
  }, [id]);

  const fetchAtlasFile = async () => {
    try {
      const data = await getAtlasFile(id);
      setAtlasFile(data);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const userName = user.user_metadata?.full_name || user.email?.split('@')[0] || '';
        if (data.author_id === user.id || (userName && data.author === userName)) {
          setIsOwner(true);
        }
      }
    } catch (error) {
      console.error('Error fetching atlas file:', error);
      navigate('/atlas');
    } finally {
      setLoading(false);
    }
  };

  const handleFork = async () => {
    try {
      const result = await forkAtlasFile(id);
      navigate(`/itinerary/${result.itinerary_id}`);
    } catch (error) {
      console.error('Error forking atlas file:', error);
      alert('Failed to fork. Make sure you are logged in and the file has a published version.');
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const text = `${atlasFile?.title} — ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareOpen(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: atlasFile?.title, url: window.location.href });
      setShareOpen(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    setShareOpen(false);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = contentRef.current;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `${atlasFile?.title || 'atlas-file'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .save();
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400"></div>
        </div>
      </PageContainer>
    );
  }

  if (!atlasFile) return null;

  const content = typeof atlasFile.content === 'string'
    ? JSON.parse(atlasFile.content)
    : atlasFile.content || {};

  const days = content.days || [];
  const config = getSourceConfig(atlasFile.source_type);
  const TypeIcon = config.icon;

  return (
    <div>
      {/* Full-viewport cover with parallax */}
      {atlasFile.cover_image_url ? (
        <div ref={coverRef} className="relative h-[60vh] md:h-[70vh] overflow-hidden">
          <motion.img
            src={atlasFile.cover_image_url}
            alt={atlasFile.title}
            className="w-full h-full object-cover"
            style={{ y: coverY, opacity: coverOpacity }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-500/70 via-charcoal-500/20 to-transparent" />
          {/* Source type badge */}
          <div className="absolute top-6 left-6 md:top-8 md:left-16 z-10">
            <span className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium backdrop-blur-sm bg-white/20 text-white border border-white/30">
              <TypeIcon size={14} />
              {config.label}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="container mx-auto max-w-4xl">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl md:text-5xl font-semibold text-white mb-4"
              >
                {atlasFile.title}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap gap-4 text-sm text-white/80"
              >
                {atlasFile.author && (
                  <span className="flex items-center gap-1"><User size={16} /><span className="opacity-70">{config.authorPrefix}</span> {atlasFile.author}</span>
                )}
                {atlasFile.destination && (
                  <span className="flex items-center gap-1"><MapPin size={16} />{atlasFile.destination}</span>
                )}
                {atlasFile.trip_length && (
                  <span className="flex items-center gap-1"><Calendar size={16} />{atlasFile.trip_length} days</span>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      ) : (
        /* No cover — fallback header */
        <div className="bg-charcoal-500 py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="mb-4">
              <span className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium bg-white/20 text-white border border-white/30 w-fit">
                <TypeIcon size={14} />
                {config.label}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold text-white mb-4">{atlasFile.title}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              {atlasFile.author && <span className="flex items-center gap-1"><User size={16} /><span className="opacity-70">{config.authorPrefix}</span> {atlasFile.author}</span>}
              {atlasFile.destination && <span className="flex items-center gap-1"><MapPin size={16} />{atlasFile.destination}</span>}
              {atlasFile.trip_length && <span className="flex items-center gap-1"><Calendar size={16} />{atlasFile.trip_length} days</span>}
            </div>
          </div>
        </div>
      )}

      <PageContainer>
        <div className="max-w-4xl mx-auto">
          {/* Back + Actions */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/atlas')}
              className="flex items-center gap-2 text-platinum-600 hover:text-charcoal-500 transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Atlas Files
            </button>
            <div className="flex items-center gap-2">
              {/* Share dropdown */}
              <div className="relative" ref={shareRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShareOpen(!shareOpen)}
                  className="gap-2"
                >
                  <Share2 size={16} /> Share
                </Button>

                {shareOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShareOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-platinum-200 py-1 w-52">
                      <button
                        onClick={() => { handleCopyLink(); setShareOpen(false); }}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                      >
                        {linkCopied ? <Check size={16} className="text-green-500" /> : <Link2 size={16} />}
                        {linkCopied ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={handleShareWhatsApp}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                      >
                        <MessageCircle size={16} />
                        Share on WhatsApp
                      </button>
                      {typeof navigator.share === 'function' && (
                        <button
                          onClick={handleNativeShare}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                        >
                          <Share2 size={16} />
                          More options...
                        </button>
                      )}
                      <div className="border-t border-platinum-200 my-1" />
                      <button
                        onClick={handleExportPDF}
                        disabled={exporting}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full disabled:opacity-50"
                      >
                        <Download size={16} />
                        {exporting ? 'Exporting...' : 'Export as PDF'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {!isOwner && (
                <Button variant="outline" size="sm" className="gap-2" onClick={handleFork}>
                  <GitFork size={16} /> Fork to My Trips
                </Button>
              )}
              {isOwner && (
                <Link to={`/atlas/edit/${id}`}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Edit size={16} /> Edit
                  </Button>
                </Link>
              )}

              <SendToConcierge
                tripTitle={atlasFile.title}
                destination={atlasFile.destination}
                tripLength={atlasFile.trip_length}
                tripLink={`${window.location.origin}/atlas/${id}`}
                source="atlas_file"
                label="Have this booked for you"
              />
            </div>
          </div>

          <div ref={contentRef}>
          {atlasFile.description && (
            <ScrollReveal>
              <p className="text-lg text-platinum-700 mb-8 leading-relaxed">
                {atlasFile.description}
              </p>
            </ScrollReveal>
          )}

          <hr className="border-platinum-200 mb-8" />

          {/* Introduction */}
          {content.intro && (
            <ScrollReveal>
              <div className="mb-10">
                <div
                  className={`prose prose-lg max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 ${config.introStyle}`}
                  dangerouslySetInnerHTML={{ __html: content.intro }}
                />
              </div>
            </ScrollReveal>
          )}

          {/* Day Sections */}
          {days.map((day, index) => (
            <ScrollReveal key={index} delay={index * 0.05}>
              <div className="mb-14">
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-12 h-12 rounded-full ${config.dayCircle.bg} ${config.dayCircle.text} flex items-center justify-center font-semibold text-lg flex-shrink-0`}>
                    {index + 1}
                  </div>
                  <h2 className={`text-2xl font-semibold text-charcoal-500 pb-2 border-b ${config.sectionDivider} flex-1`}>
                    {day.title}
                  </h2>
                </div>

                {day.content && (
                  <div
                    className="prose prose-lg max-w-none mb-4 pl-16 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                    dangerouslySetInnerHTML={{ __html: day.content }}
                  />
                )}

                {day.images && day.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pl-16">
                    {day.images.map((img, imgIdx) => (
                      <motion.button
                        key={imgIdx}
                        onClick={() => setLightbox({ images: day.images, index: imgIdx })}
                        className="relative overflow-hidden rounded-xl focus:outline-none"
                        whileHover={{ scale: 1.03 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      >
                        <img
                          src={img}
                          alt={`${day.title} photo ${imgIdx + 1}`}
                          className="w-full h-48 object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollReveal>
          ))}

          {/* Tips */}
          {content.tips && (
            <ScrollReveal>
              <div className={`mb-12 ${config.tipsSection.bg} rounded-2xl p-6 border ${config.tipsSection.border}`}>
                <h2 className="text-2xl font-semibold text-charcoal-500 mb-4">
                  Travel Tips
                </h2>
                <div
                  className="prose prose-lg max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                  dangerouslySetInnerHTML={{ __html: content.tips }}
                />
              </div>
            </ScrollReveal>
          )}
          </div>{/* end contentRef */}
        </div>
      </PageContainer>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          {/* Prev */}
          {lightbox.images.length > 1 && lightbox.index > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index - 1 })); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          {lightbox.images.length > 1 && lightbox.index < lightbox.images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index + 1 })); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Close */}
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <X size={20} />
          </button>

          {/* Counter */}
          {lightbox.images.length > 1 && (
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {lightbox.index + 1} / {lightbox.images.length}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default AtlasFileViewPage;
